/** W82 Bewegung (plan-w82.md 3.7, E9): Dauern und Kurven nur aus tokens-w82.json. Jede Bewegung fragt beim Start
 *  `bewegungReduziert()` und `still()`, nicht nur das Stylesheet: reduziert springen Lage, Maßstab und Drehung in den
 *  Endzustand, Blenden dauern höchstens 120 ms; `?still` zeigt alles sofort (nur für Bilder und Tests). */
import tokens from "./tokens-w82.json";
import type { Fassung } from "./einstellungen";

export const DAUER = tokens.dauern;

/** Kurven als CSS-Werte; `feder` gibt `kurve("feder")` als linear() zurück, wo der Browser es kann. */
export const KURVE = {
  expo: tokens.kurven.expo,
  feder: tokens.kurven.feder,
  federLinear: tokens.kurven.federLinear,
  sanft: tokens.kurven.sanft,
  mit: tokens.kurven.mit,
  landen: tokens.kurven.landen,
  vorhang: tokens.kurven.vorhang,
  weg: tokens.kurven.weg,
} as const;
export type KurvenName = keyof typeof KURVE;

/** Dauern der Bedienung ohne eigenen Schlüssel in tokens.json (Auftrag W82-Web-1 Umfang 4, DESIGN-W82 „Wechsel und
 *  Bedienung“; dieselben Werte stehen als Ableitung in welt-tokens.mjs): Reiterlicht 550, Sortier- und Segmentlicht 500,
 *  Schalterknopf 450, Bahn 300, Hinweis Deckkraft 300 und Lage 500. */
export const DAUER_BEDIENUNG = { reiterLicht: 550, segmentLicht: 500, schalterKnopf: 450, schalterBahn: 300, hinweisBlende: 300, hinweisLage: 500 } as const;

/** Eine Kurve als Liste der vier Stützwerte (für Motion), etwa `[0.16, 1, 0.3, 1]` für expo. */
export function kurveAlsListe(name: Exclude<KurvenName, "federLinear">): [number, number, number, number] {
  const z = /cubic-bezier\(([^)]+)\)/.exec(KURVE[name])?.[1]?.split(",").map(Number) ?? [0, 0, 1, 1];
  return [z[0] ?? 0, z[1] ?? 0, z[2] ?? 1, z[3] ?? 1];
}

/** Höchstdauer einer Blende bei reduzierter Bewegung (E9, Bestand --dur-reduced-fade). */
export const BLENDE_REDUZIERT = 120;
/** W82: Deckkraft und Farbe blenden bei reduzierter Bewegung 150 ms statt hartem Schnitt (DESIGN-W82 „Reduzierte Bewegung“,
 *  FW22 Regel 6, D-490); neue Stellen ab 5h-W2 nehmen diesen Wert. */
export const BLENDE_REDUZIERT_W82 = 150;

export function kurve(name: KurvenName): string {
  if (name === "feder" && typeof CSS !== "undefined" && CSS.supports?.("transition-timing-function", "linear(0, 1)")) return KURVE.federLinear;
  return KURVE[name];
}

/** Kurve je Rolle (tokens.json kurven.verwendung): lebhaft Feder für Knöpfe, Blasen, Pillen und Schalter, sonst expo;
 *  ruhig nie Feder. Dieselbe Zuordnung steht in tokens.css (--pgw-kurve-knopf …). */
export type KurvenRolle = "knopf" | "blase" | "pille" | "schalter" | "licht";
export function kurveFuer(rolle: KurvenRolle, fassung: Fassung): "feder" | "expo" {
  if (fassung === "ruhig" || rolle === "licht") return "expo";
  return "feder";
}

export function bewegungReduziert(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Prüfschalter `?still` an der Adresse: alles sofort im Endzustand, ohne die 120 ms. */
export function still(): boolean {
  return typeof location !== "undefined" && new URLSearchParams(location.search).has("still");
}

type Bild = Keyframe;

/** Endzustand eines Bilds als Inline-Stil (nur Lage, Maßstab, Drehung, Deckkraft). */
function endzustand(el: HTMLElement | SVGElement, bilder: Bild[]): void {
  const ende = bilder[bilder.length - 1];
  if (!ende) return;
  if (ende.transform !== undefined) el.style.transform = String(ende.transform);
  if (ende.opacity !== undefined) el.style.opacity = String(ende.opacity);
}

/** Spielt eine Bewegung (Web Animations API) und lässt das Element im Endzustand. `art: "lage"` (Lage, Maßstab,
 *  Drehung) springt bei reduzierter Bewegung sofort ans Ende, `art: "blende"` (nur Deckkraft) dauert dann höchstens
 *  120 ms; bei `?still` springt beides. Rückgabe die laufende Animation oder null. */
export function spiel(
  el: HTMLElement | SVGElement | null | undefined,
  bilder: Bild[],
  opt: { dauer: number; kurve: string; art: "lage" | "blende"; verzug?: number },
): Animation | null {
  if (!el) return null;
  endzustand(el, bilder);
  if (still()) return null;
  const reduziert = bewegungReduziert();
  if (reduziert && opt.art === "lage") return null;
  if (typeof el.animate !== "function") return null;
  const dauer = reduziert ? Math.min(opt.dauer, BLENDE_REDUZIERT) : opt.dauer;
  return el.animate(bilder, { duration: dauer, easing: reduziert ? "linear" : opt.kurve, delay: reduziert ? 0 : (opt.verzug ?? 0), fill: "backwards" });
}

export type FlugArt = "flug" | "rundflug" | "heimweg" | "erzaehlen";

const begrenzt = (wert: number, unten: number, oben: number) => Math.min(oben, Math.max(unten, wert));

/** Dauer einer Flugstrecke in ms: lebhaft 260 + 0,6 x Weg (300 bis 950), Rundflug x 0,8, Heimweg x 0,85; ruhig
 *  340 + 0,5 x Weg (460 bis 1100); Erzählflug der Einführung 1,5 x lebhaft (600 bis 1400, E18). */
export function flugDauer(weg: number, fassung: Fassung, art: FlugArt = "flug"): number {
  if (art === "erzaehlen") return begrenzt(1.5 * (260 + 0.6 * weg), 600, 1400);
  const strecke = fassung === "ruhig" ? begrenzt(340 + 0.5 * weg, 460, 1100) : begrenzt(260 + 0.6 * weg, 300, 950);
  return strecke * (art === "rundflug" ? 0.8 : art === "heimweg" ? 0.85 : 1);
}

/** Bogen zur Seite in px: lebhaft bis 70 (22 % des Wegs), ruhig bis 44 (15 %). */
export function flugBogen(weg: number, fassung: Fassung): number {
  return fassung === "ruhig" ? Math.min(44, 0.15 * weg) : Math.min(70, 0.22 * weg);
}

/** Neigung in Flugrichtung höchstens 12 (ruhig 8) Grad. */
export function flugNeigung(dx: number, fassung: Fassung): number {
  const grenze = fassung === "ruhig" ? 8 : 12;
  return begrenzt(dx / 14, -grenze, grenze);
}

/** Mitte des ersten sichtbaren Geists im Bild, sonst null. */
export function geistMitte(): { x: number; y: number } | null {
  for (const g of document.querySelectorAll("svg.geist")) {
    const r = g.getBoundingClientRect();
    if (r.width > 0 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth) return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }
  return null;
}

/** Wechsel von Farben oder Erscheinung als Lichtkreis (View Transitions) aus der Mitte des Geists, sonst aus der
 *  Bildmitte: lebhaft 780, ruhig 820 ms `vorhang`. Reduziert eine Blende von 120 ms, `?still` und ohne View
 *  Transitions sofort. */
export function lichtkreis(aendern: () => void, mitte: { x: number; y: number } | null = geistMitte()): void {
  const doc = document as Document & { startViewTransition?: (f: () => void) => { ready: Promise<void>; finished: Promise<void> } };
  if (still() || typeof doc.startViewTransition !== "function") {
    aendern();
    return;
  }
  const wurzel = document.documentElement;
  const fassung: Fassung = wurzel.dataset.fassung === "ruhig" ? "ruhig" : "lebhaft";
  const reduziert = bewegungReduziert();
  wurzel.classList.add("pgw-lichtkreis");
  const t = doc.startViewTransition(aendern);
  t.ready
    .then(() => {
      if (reduziert) {
        wurzel.animate({ opacity: [0, 1] }, { duration: BLENDE_REDUZIERT, easing: "linear", pseudoElement: "::view-transition-new(root)" });
        return;
      }
      const m = mitte ?? { x: innerWidth / 2, y: innerHeight / 2 };
      const r = Math.hypot(Math.max(m.x, innerWidth - m.x), Math.max(m.y, innerHeight - m.y));
      wurzel.animate(
        { clipPath: [`circle(0px at ${m.x}px ${m.y}px)`, `circle(${r}px at ${m.x}px ${m.y}px)`] },
        { duration: DAUER.wechsel.lichtkreis[fassung], easing: KURVE.vorhang, pseudoElement: "::view-transition-new(root)" },
      );
    })
    .catch(() => undefined);
  t.finished.finally(() => wurzel.classList.remove("pgw-lichtkreis")).catch(() => undefined);
}
